import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, Building2, Check, ShieldCheck, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/ga4";

interface PricingSectionProps {
  showHeader?: boolean;
}

export const PricingSection = ({ showHeader = true }: PricingSectionProps) => {
  const { language } = useLanguage();

  const content = {
    fr: {
      eyebrow: "Les offres",
      title: "Trois plans pour trois niveaux de maîtrise",
      subtitle:
        "Starter pose la base. Pro ouvre le vrai pilotage local. Business apporte la structure nécessaire aux équipes multi-établissements.",
      perMonth: "/mois",
      trialNote: "14 jours offerts. Sans engagement.",
      proReason:
        "C'est le bon choix si vous voulez plus qu'un simple suivi des avis. Pro vous aide à mieux répondre, mieux comprendre votre présence locale et mieux piloter ce que vos futurs clients voient de vous.",
      businessFor:
        "Le bon niveau pour les structures qui gèrent plusieurs établissements, plusieurs équipes et plusieurs priorités en même temps.",
      plans: [
        {
          name: "Starter",
          label: "Avis et réponses",
          fit: "Pour poser une base sérieuse sur les avis et le rythme de réponse.",
          price: "49€",
          icon: ShieldCheck,
          highlighted: false,
          cta: "Choisir Starter",
          link: "/signup?plan=starter",
          features: [
            { text: "Centralisation des avis", included: true },
            { text: "Réponses IA avec validation humaine", included: true },
            { text: "QR code et collecte de feedback", included: true },
            { text: "Cockpit réputation", included: true },
            { text: "Audit GBP complet", included: false, note: "À partir de Pro" },
            { text: "Visibilité locale", included: false, note: "À partir de Pro" },
            { text: "Concurrents locaux", included: false, note: "À partir de Pro" },
          ],
        },
        {
          name: "Pro",
          label: "Avis, présence locale et fiches établissement",
          fit: "Le plan stratégique pour piloter vraiment la zone locale.",
          price: "99€",
          icon: Sparkles,
          highlighted: true,
          cta: "Choisir Pro",
          link: "/signup?plan=pro",
          badge: "Plan stratégique",
          features: [
            { text: "Tout Starter inclus", included: true },
            { text: "Audit GBP complet", included: true },
            { text: "Visibilité locale", included: true },
            { text: "Concurrents locaux", included: true },
            { text: "Réputation multi-plateforme", included: true },
            { text: "Analytics plus avancés", included: true },
          ],
        },
        {
          name: "Business",
          label: "Pilotage avancé multi-établissements",
          fit: "Pour les groupes, réseaux et opérateurs exigeants.",
          price: "199€",
          icon: Building2,
          highlighted: false,
          cta: "Choisir Business",
          link: "/signup?plan=business",
          features: [
            { text: "Tout Pro inclus", included: true },
            { text: "Jusqu'à 5 établissements", included: true },
            { text: "Historique renforcé", included: true },
            { text: "Analyses plus avancées", included: true },
            { text: "Pilotage multi-sites", included: true },
            { text: "Fonctionnalités premium", included: true },
          ],
        },
      ],
    },
    en: {
      eyebrow: "Plans",
      title: "Three plans for three levels of control",
      subtitle:
        "Starter builds the foundation. Pro unlocks real local control. Business adds the structure multi-location teams need.",
      perMonth: "/month",
      trialNote: "14 days free. No commitment.",
      proReason:
        "It is the right choice if you want more than simple review tracking. Pro helps you reply better, understand your local presence better, and manage what future customers see before they contact you.",
      businessFor:
        "The right level for organizations managing multiple locations, multiple teams, and multiple priorities at the same time.",
      plans: [
        {
          name: "Starter",
          label: "Reviews and replies",
          fit: "For building a serious foundation around reviews and reply rhythm.",
          price: "$49",
          icon: ShieldCheck,
          highlighted: false,
          cta: "Choose Starter",
          link: "/signup?plan=starter",
          features: [
            { text: "Review centralization", included: true },
            { text: "AI replies with human approval", included: true },
            { text: "QR code and feedback collection", included: true },
            { text: "Reputation cockpit", included: true },
            { text: "Full GBP audit", included: false, note: "From Pro" },
            { text: "Local visibility", included: false, note: "From Pro" },
            { text: "Local competitors", included: false, note: "From Pro" },
          ],
        },
        {
          name: "Pro",
          label: "Reviews, local presence, and business profiles",
          fit: "The strategic plan for managing local presence properly.",
          price: "$99",
          icon: Sparkles,
          highlighted: true,
          cta: "Choose Pro",
          link: "/signup?plan=pro",
          badge: "Strategic plan",
          features: [
            { text: "Everything in Starter", included: true },
            { text: "Full GBP audit", included: true },
            { text: "Local visibility", included: true },
            { text: "Local competitors", included: true },
            { text: "Cross-platform reputation", included: true },
            { text: "More advanced analytics", included: true },
          ],
        },
        {
          name: "Business",
          label: "Advanced multi-location control",
          fit: "For groups, networks and demanding operators.",
          price: "$199",
          icon: Building2,
          highlighted: false,
          cta: "Choose Business",
          link: "/signup?plan=business",
          features: [
            { text: "Everything in Pro", included: true },
            { text: "Up to 5 locations", included: true },
            { text: "Extended history", included: true },
            { text: "More advanced analysis", included: true },
            { text: "Multi-site management", included: true },
            { text: "Premium features", included: true },
          ],
        },
      ],
    },
  };

  const page = content[language];

  return (
    <section id="pricing" className="py-20 md:py-24">
      <div className="container mx-auto px-4">
        {showHeader && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mx-auto max-w-3xl text-center"
          >
            <Badge variant="outline" className="rounded-full px-4 py-1.5 text-xs font-medium">
              {page.eyebrow}
            </Badge>
            <h2 className="mt-5 text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
              {page.title}
            </h2>
            <p className="mt-4 text-lg leading-8 text-muted-foreground">{page.subtitle}</p>
          </motion.div>
        )}

        <div className={`mx-auto grid max-w-6xl gap-6 lg:grid-cols-3 lg:items-stretch ${showHeader ? "mt-12" : ""}`}>
          {page.plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: index * 0.08 }}
              className={`relative flex flex-col rounded-[2rem] border p-8 shadow-sm transition-transform duration-300 ${
                plan.highlighted
                  ? "border-primary bg-card shadow-xl shadow-primary/10 lg:-translate-y-2"
                  : "border-border bg-card"
              }`}
            >
              {plan.badge && (
                <div className="absolute -top-3 left-6">
                  <Badge className="rounded-full px-4 py-1.5 text-xs font-medium">{plan.badge}</Badge>
                </div>
              )}

              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10">
                <plan.icon className="h-5 w-5 text-primary" />
              </div>

              <div className="mt-6">
                <p className="text-sm font-medium text-primary">{plan.label}</p>
                <h3 className="mt-2 text-2xl font-semibold text-foreground">{plan.name}</h3>
                <p className="mt-2 text-sm font-medium text-foreground/80">{plan.fit}</p>
              </div>

              <div className="mt-6 border-t border-border pt-6">
                <div className="flex items-end gap-2">
                  <span className="text-5xl font-semibold tracking-tight text-foreground">{plan.price}</span>
                  <span className="pb-1 text-base text-muted-foreground">{page.perMonth}</span>
                </div>
              </div>

              <ul className="mt-6 flex-1 space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature.text} className="flex items-start gap-3">
                    <div className={`mt-0.5 flex h-5 w-5 items-center justify-center rounded-full ${feature.included ? "bg-accent/10" : "bg-muted"}`}>
                      {feature.included ? (
                        <Check className="h-3.5 w-3.5 text-accent" strokeWidth={3} />
                      ) : (
                        <X className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={3} />
                      )}
                    </div>
                    <div className="min-w-0 flex-1">
                      <span className={`text-sm leading-6 ${feature.included ? "text-foreground" : "text-muted-foreground line-through decoration-muted-foreground/70"}`}>
                        {feature.text}
                      </span>
                      {!feature.included && 'note' in feature && feature.note ? (
                        <span className="ml-2 inline-flex rounded-full border border-border bg-muted px-2 py-0.5 text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                          {feature.note}
                        </span>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>

              <Button asChild size="lg" className={`mt-8 h-12 w-full text-base ${plan.highlighted ? "btn-premium" : ""}`} variant={plan.highlighted ? "default" : "outline"} onClick={() => trackEvent(`click_plan_${plan.name.toLowerCase()}` as any, { language, plan_name: plan.name, page_path: window.location.pathname })}>
                <Link to={plan.link}>
                  {plan.cta}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>

              <p className="mt-3 text-center text-xs text-muted-foreground">{page.trialNote}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
